import { Component, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * React class-based error boundary.
 * Catches synchronous render errors to prevent full-page blank screen.
 * Surfaces the error message for diagnosis, includes a Reset button.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (fallback) return fallback(error, this.reset);
      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] p-6 rounded-lg border border-destructive/30 bg-destructive/5 gap-3">
          <p className="font-semibold text-destructive">Something went wrong</p>
          <p className="text-sm text-muted-foreground font-mono break-all text-center max-w-lg">
            {error.message}
          </p>
          <Button variant="outline" size="sm" onClick={this.reset}>
            Try again
          </Button>
        </div>
      );
    }

    return children;
  }
}
